import { useState, type RefObject } from 'react';
import { Link } from 'react-router-dom';
import { useEquipment, useEquip, useTransmog, type EquipmentStateDto } from '../api/equipment';
import { useBag, type BagItemDto } from '../api/bag';
import { useWallet } from '../api/wallet';
import type { CompanionDto } from '../api/companions';
import { ApiClientError } from '../api/client';
import { BottomSheet, Dialog } from '../components/BottomSheet';
import { itemIconPath } from '../compositor/assetPaths';
import { SLOT_DEFS, STAT_DEFS } from './equipmentLabels';
import { benchLinksFor, sourceLineFor } from './benchFor';
import { slotLabel } from '../lib/slotLabel';
import { ShellGlyph } from '../shell/glyphs';

export type EquipmentSheetProps = {
  companion: CompanionDto;
  onClose: () => void;
  /** The button that opened this sheet — see BottomSheet.tsx's own doc comment on why this is
   *  needed for reliable focus-return. */
  restoreFocusRef?: RefObject<HTMLElement | null>;
  /** Gallery-only — see BottomSheet.tsx's own doc comment. */
  portalContainer?: HTMLElement | null;
  variant?: 'sheet' | 'overlay';
};

// SLOT_DEFS and the stat labels moved to ./equipmentLabels when the Bag's own ItemDetailSheet
// grew an equipment block — see that module's header for why they are shared rather than
// copied. `slotLabel` is the whole game's one slot-code-to-label transform (lib/slotLabel);
// it yields the same six words this module's own lookup used to.
const STATS = STAT_DEFS as { key: keyof EquipmentStateDto['effectiveStats']; label: string }[];

// TransmogService.TRANSMOG_FEE (server-side constant, equipment.ts's own postTransmog doc
// comment) — mirrored here as UI copy, same "server owns the number, client mirrors it as
// display text" precedent as RESPEC_COST_SHELLS in AllocateSheet.tsx.
export const TRANSMOG_FEE_SHELLS = 75;

const RARITY_ORDER = ['common', 'uncommon', 'rare', 'radiant', 'mythic'];
function rarityRank(rarity: string): number {
  const i = RARITY_ORDER.indexOf(rarity);
  return i < 0 ? 0 : i;
}

function rarityLabel(rarity: string): string {
  return rarity.charAt(0).toUpperCase() + rarity.slice(1);
}

const GENERIC_ERROR = 'Something went wrong — please try again.';

// Server rejection codes (GlobalExceptionHandler.java / EquipmentController's own — see
// equipment.ts's putEquipment doc comment) mapped to friendly, C3-neutral copy — same pattern
// WardrobeScreen/FeedSheet already use for their own ERROR_MESSAGES.
function friendlyEquipError(err: unknown): string {
  if (err instanceof ApiClientError) {
    switch (err.code) {
      case 'equipment_level_locked': return 'This companion needs a higher level to use that.';
      case 'item_already_equipped': return 'That item is already equipped on another companion.';
      case 'not_owned': return "You don't own that anymore.";
      case 'slot_mismatch': return "That doesn't fit this slot.";
      default: return GENERIC_ERROR;
    }
  }
  return GENERIC_ERROR;
}

function friendlyTransmogError(err: unknown): string {
  if (err instanceof ApiClientError) {
    switch (err.code) {
      case 'no_transmog_art': return "That item doesn't have transmog art to show.";
      case 'insufficient_funds': return 'Not enough Shells for that.';
      default: return GENERIC_ERROR;
    }
  }
  return GENERIC_ERROR;
}

/**
 * SCREENS.md "Equipment sheet" — opened from a companion card's "Equip" button (HabitatScreen's
 * CompanionCard), same overlay-on-Habitat convention as `AllocateSheet`. One sheet, three swapped
 * body views (never a stacked second `BottomSheet` — mirrors AllocateSheet's own respec-inline-
 * confirm precedent rather than nesting Radix dialogs):
 *
 * - slot list (default): 6 rows, each equipped item or "Empty", tap → picker; equipped rows also
 *   get an inline "Unequip" action and, when the item has linked transmog art (`hasTransmog`), a
 *   toggle. Bottom-anchored effective-stats block (base → +bonus = total, per stat) — `base` comes
 *   straight off the `companion` prop (the same `useCompanions()` list HabitatScreen already
 *   fetched, no second read), `total` off this sheet's own `useEquipment` DTO.
 * - items picker: bag equipment matching the tapped slot (`category === 'equipment' && slot ===
 *   row.slot`, client-filtered from `useBag()` — GET /bag already returns the whole inventory),
 *   sorted rarity desc then name. A level-locked item (`levelReq` vs. `companion.level`) renders
 *   disabled with "Needs level N", never hidden. Every equip/unequip attempt — from here OR the
 *   slot list's own Unequip button — returns to the slot list on completion, success or failure;
 *   a failure's friendly message shows inline on THAT slot's own row (`slotErrors`), per the brief.
 * - transmog confirm: only reachable by turning a toggle ON (turning OFF is free, applied
 *   directly, no confirm needed — see `postTransmog`'s own doc comment on why). States the fee and
 *   the live wallet balance (`useWallet`, same HUD query every other fee confirm in this app
 *   reads), Confirm disabled when short — same `insufficientReason` shape as AllocateSheet's
 *   respec confirm and ShopScreen's Buy row.
 *
 * `useEquip`/`useTransmog` are plain react-query mutations (not optimistic — equipment.ts's own
 * doc comment: real, unpredictable server refusals and currency spend), so every outcome here is
 * awaited via `mutateAsync` and branched on catching `ApiClientError`, exactly like
 * AllocateSheet/FeedSheet/WardrobeScreen already do — never a `.ok`-style typed result (that
 * shape belongs to battles.ts's turn resolution, a different module).
 *
 * Built on `BottomSheet` (`@radix-ui/react-dialog` — FABLE5_BUILD_PROMPT.md §3).
 */
export function EquipmentSheet({ companion, onClose, restoreFocusRef, portalContainer, variant = 'sheet' }: EquipmentSheetProps) {
  const equipment = useEquipment(companion.id);
  const bag = useBag();
  const wallet = useWallet();
  const equip = useEquip();
  const transmog = useTransmog();

  const [pickerSlot, setPickerSlot] = useState<string | null>(null);
  const [slotErrors, setSlotErrors] = useState<Partial<Record<string, string>>>({});
  const [transmogConfirmSlot, setTransmogConfirmSlot] = useState<string | null>(null);
  const [transmogError, setTransmogError] = useState<string | null>(null);

  function clearSlotError(slot: string) {
    setSlotErrors((prev) => {
      if (!(slot in prev)) return prev;
      const next = { ...prev };
      delete next[slot];
      return next;
    });
  }

  function openPicker(slot: string) {
    clearSlotError(slot);
    setPickerSlot(slot);
  }

  // Every equip/unequip attempt — a picker tap or the slot list's own Unequip button — funnels
  // through here and always returns to the slot list, success or failure; a failure's message
  // surfaces on that slot's own row (§ this component's own header comment).
  async function handleEquip(slot: string, itemCode: string | null) {
    try {
      await equip.mutateAsync({ companionId: companion.id, slot, itemCode });
      clearSlotError(slot);
    } catch (err) {
      setSlotErrors((prev) => ({ ...prev, [slot]: friendlyEquipError(err) }));
    } finally {
      setPickerSlot(null);
    }
  }

  async function handleTransmogOff(slot: string) {
    try {
      await transmog.mutateAsync({ companionId: companion.id, slot, visible: false });
      clearSlotError(slot);
    } catch (err) {
      setSlotErrors((prev) => ({ ...prev, [slot]: friendlyTransmogError(err) }));
    }
  }

  async function handleTransmogConfirm() {
    if (!transmogConfirmSlot || transmog.isPending) return;
    setTransmogError(null);
    try {
      await transmog.mutateAsync({ companionId: companion.id, slot: transmogConfirmSlot, visible: true });
      clearSlotError(transmogConfirmSlot);
      setTransmogConfirmSlot(null);
    } catch (err) {
      setTransmogError(friendlyTransmogError(err));
    }
  }

  const walletShells = wallet.data?.shells;
  const transmogShortfall = walletShells != null ? TRANSMOG_FEE_SHELLS - walletShells : 0;
  const transmogInsufficientReason = walletShells != null && transmogShortfall > 0
    ? `You need ${transmogShortfall} more Shells` : null;

  const pickerItems: BagItemDto[] = pickerSlot && bag.data
    ? bag.data.items
        .filter((i) => i.category === 'equipment' && i.slot === pickerSlot)
        .sort((a, b) => rarityRank(b.rarity) - rarityRank(a.rarity) || a.name.localeCompare(b.name))
    : [];

  const heading = transmogConfirmSlot
    ? 'Confirm transmog'
    : pickerSlot ? slotLabel(pickerSlot) : `Equip ${companion.name}`;

  return (
    <BottomSheet
      open
      onOpenChange={(next) => { if (!next) onClose(); }}
      contentClassName="equipment-sheet"
      restoreFocusRef={restoreFocusRef}
      portalContainer={portalContainer}
      variant={variant}
    >
      <div className="sheet-header">
        <Dialog.Title asChild>
          <h2>{heading}</h2>
        </Dialog.Title>
        <Dialog.Close asChild>
          <button type="button" className="sheet-close" aria-label="Close equipment sheet">
            Close
          </button>
        </Dialog.Close>
      </div>
      <Dialog.Description className="sr-only">
        {companion.name}&rsquo;s equipped weapon, offhand, head, body, trinket, and charm slots.
      </Dialog.Description>

      <div className="sheet-body">
        {equipment.isLoading && (
          <ul className="equipment-slot-list" aria-hidden="true">
            {SLOT_DEFS.map(({ code }) => <li key={code} className="skeleton equipment-slot-skeleton" />)}
          </ul>
        )}

        {equipment.isError && (
          <div className="retry-card">
            <p>Couldn&rsquo;t reach Project Pets.</p>
            <button type="button" className="btn-primary" onClick={() => equipment.refetch()}>Try again</button>
          </div>
        )}

        {equipment.isSuccess && !pickerSlot && !transmogConfirmSlot && (
          <>
            <ul className="equipment-slot-list">
              {SLOT_DEFS.map(({ code, label }) => {
                const entry = equipment.data.slots[code];
                return (
                  <li key={code} className={`equipment-slot-row${entry ? ` rarity-${entry.rarity}` : ''}`}>
                    <button type="button" className="equipment-slot-tap" onClick={() => openPicker(code)}>
                      {entry ? (
                        <img src={itemIconPath(entry.itemCode)} alt="" className="equipment-slot-icon" />
                      ) : (
                        <div className="equipment-slot-icon-empty" aria-hidden="true" />
                      )}
                      <span className="equipment-slot-info">
                        <span className="equipment-slot-label">{label}</span>
                        {entry ? (
                          <>
                            <span className="equipment-slot-item-name">{entry.name}</span>
                            <span className="item-cell-rarity">{rarityLabel(entry.rarity)}</span>
                          </>
                        ) : (
                          <>
                            <span className="equipment-slot-empty">Empty</span>
                            {/* R3 (owner ruling, no new worn art): an empty slot says where gear
                                for it is actually made, instead of just "Empty" and nothing else. */}
                            <span className="equipment-slot-source">{sourceLineFor(code)}</span>
                          </>
                        )}
                      </span>
                    </button>

                    {entry && (
                      <div className="equipment-slot-actions">
                        <button
                          type="button"
                          className="btn-quiet"
                          disabled={equip.isPending}
                          onClick={() => handleEquip(code, null)}
                        >
                          Unequip
                        </button>
                        {entry.hasTransmog && (
                          <button
                            type="button"
                            className="btn-quiet"
                            aria-pressed={entry.transmogVisible}
                            disabled={transmog.isPending}
                            onClick={() => (entry.transmogVisible ? handleTransmogOff(code) : setTransmogConfirmSlot(code))}
                          >
                            {entry.transmogVisible ? 'Hide transmog art' : 'Show transmog art'}
                          </button>
                        )}
                      </div>
                    )}

                    {slotErrors[code] && <p className="field-error" role="alert">{slotErrors[code]}</p>}
                  </li>
                );
              })}
            </ul>

            <div className="equipment-stats-block">
              <h3>Effective stats</h3>
              <ul className="equipment-stats-list">
                {STATS.map(({ key, label }) => {
                  const base = companion.stats[key];
                  const total = equipment.data.effectiveStats[key];
                  const delta = total - base;
                  return (
                    <li key={key} className="equipment-stats-row">
                      <span className="equipment-stats-name">{label}</span>
                      <span className="equipment-stats-value">
                        {base} {delta >= 0 ? `+${delta}` : delta} = {total}
                      </span>
                    </li>
                  );
                })}
              </ul>
            </div>
          </>
        )}

        {equipment.isSuccess && pickerSlot && (
          <div className="equipment-picker">
            <button type="button" className="btn-quiet equipment-picker-back" onClick={() => setPickerSlot(null)}>
              ← Back
            </button>

            {bag.isLoading && <p className="form-note">Loading your bag…</p>}
            {bag.isError && (
              <div className="retry-card">
                <p>Couldn&rsquo;t reach Project Pets.</p>
                <button type="button" className="btn-primary" onClick={() => bag.refetch()}>Try again</button>
              </div>
            )}

            {bag.isSuccess && (
              pickerItems.length === 0 ? (
                // R3: the same "gear comes from a bench" copy as the empty slot row, plus a way
                // there — clicking through closes the sheet (GiftFlowSheet's own "Find friends" /
                // "Go to the Thatchwell Market" links set this onClick={onClose} precedent).
                <div className="drawer-empty">
                  <p className="drawer-empty-title">Nothing fits this slot yet</p>
                  <p className="equipment-slot-source">{sourceLineFor(pickerSlot)}</p>
                  {benchLinksFor(pickerSlot).map((link) => (
                    <Link key={link.href} to={link.href} className="drawer-empty-link" onClick={onClose}>
                      {link.label}
                    </Link>
                  ))}
                </div>
              ) : (
                <ul className="equipment-picker-list">
                  {pickerItems.map((item) => {
                    const locked = item.levelReq != null && companion.level < item.levelReq;
                    // QA 2026-09-23 (battle F11): the piece already IN this slot used to be one
                    // more plain "Equip" row. It reads as worn now, and its action is the one
                    // that makes sense for it — the same Unequip the slot list offers.
                    const worn = equipment.data.slots[pickerSlot]?.itemCode === item.code;
                    return (
                      <li key={item.code} className={`equipment-picker-row rarity-${item.rarity}${worn ? ' equipment-picker-row-worn' : ''}`}>
                        <img src={itemIconPath(item.code)} alt="" className="equipment-picker-icon" />
                        <div className="equipment-picker-info">
                          <span className="equipment-picker-name">{item.name}</span>
                          <span className="item-cell-rarity">{rarityLabel(item.rarity)}</span>
                          {worn && <span className="equipment-picker-worn">✓ Equipped</span>}
                          {locked && <span className="equipment-picker-locked-hint">Needs level {item.levelReq}</span>}
                        </div>
                        <button
                          type="button"
                          className="btn-quiet equipment-picker-action"
                          disabled={locked || equip.isPending}
                          onClick={() => handleEquip(pickerSlot, worn ? null : item.code)}
                        >
                          {equip.isPending ? (worn ? 'Removing…' : 'Equipping…') : worn ? 'Unequip' : 'Equip'}
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )
            )}
          </div>
        )}

        {equipment.isSuccess && transmogConfirmSlot && (
          <div className="equipment-transmog-confirm">
            <button
              type="button"
              className="btn-quiet"
              onClick={() => { setTransmogConfirmSlot(null); setTransmogError(null); }}
            >
              ← Back
            </button>
            <p>Show transmog art for {TRANSMOG_FEE_SHELLS} Shells?</p>
            <p className="equipment-transmog-balance">
              <ShellGlyph /> Balance: {walletShells ?? '—'} Shells
            </p>
            {transmogInsufficientReason && <p className="field-error" role="alert">{transmogInsufficientReason}</p>}
            {transmogError && <p className="field-error" role="alert">{transmogError}</p>}
            <div className="equipment-transmog-actions">
              <button type="button" className="btn-quiet" disabled={transmog.isPending} onClick={() => setTransmogConfirmSlot(null)}>
                Cancel
              </button>
              <button
                type="button"
                className="btn-primary"
                disabled={transmog.isPending || transmogInsufficientReason != null}
                onClick={handleTransmogConfirm}
              >
                {transmog.isPending ? 'Saving…' : (<><ShellGlyph /> Confirm</>)}
              </button>
            </div>
          </div>
        )}
      </div>
    </BottomSheet>
  );
}
